/**
 * The trip's price receipt, for admins (founder, 2026-10-28, after VO1SVD:
 * "so that we can always go back in time").
 *
 * booking_price_receipts is written by the database when the booking is
 * saved (migration 201) and never changes after its first hour. This card
 * reads it from GET /api/admin/bookings/:id/price-receipt (server/price-receipt.js):
 * what was searched, the fare booked, every price, the server's own price
 * check at checkout, the travelers with their ages, the account's searches
 * before booking and the fares shown for the flight.
 *
 * Closed by default: one row. When the travelers on the booking don't match
 * the travelers its fare was priced for, the closed row turns amber and says
 * so, so it's seen without opening anything. Read-only.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Check, ChevronDown, Loader2, Lock, Receipt } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { RECEIPTS_STARTED_AT, searchedSummary, travelerLine, travelerSummary } from './priceReceiptText';

const money = (cents) => (Number.isFinite(cents)
  ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—');
const wholeMoney = (cents) => (Number.isFinite(cents) ? `$${Math.round(cents / 100).toLocaleString('en-US')}` : '—');
const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const timeOnly = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const shortDay = (day) => {
  if (!day) return '';
  const d = new Date(`${String(day).slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const cap = (v) => (v ? `${String(v).charAt(0).toUpperCase()}${String(v).slice(1).replace(/_/g, ' ')}` : '');

const SOURCE_LABEL = {
  checkout: 'the checkout',
  search: 'the search the fare came from',
  cart: 'the search the cart carried',
};

const QUOTE_BADGE = {
  passed: { label: 'Passed', cls: 'bg-emerald-500/[0.12] text-emerald-300' },
  flagged: { label: 'Flagged', cls: 'bg-amber-500/[0.12] text-amber-200' },
  refused: { label: 'Refused', cls: 'bg-rose-500/[0.12] text-rose-300' },
};

function Box({ label, children }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
      <div className="text-white/50 text-[11.5px] font-normal mb-1">{label}</div>
      {children}
    </div>
  );
}
Box.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

function Price({ label, cents, strong = false }) {
  return (
    <div className="rounded-lg bg-white/[0.03] px-2.5 py-2">
      <div className="text-white/45 text-[11px] font-normal">{label}</div>
      <div className={`text-white text-[14px] mt-0.5 ${strong ? 'font-medium' : 'font-normal'}`}>{money(cents)}</div>
    </div>
  );
}
Price.propTypes = { label: PropTypes.string.isRequired, cents: PropTypes.number, strong: PropTypes.bool };

function Fold({ label, aside = null, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pt-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-white/70 text-[12.5px] font-normal hover:text-white transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          {label}
        </span>
        {aside ? <span className="text-white/40">{aside}</span> : null}
      </button>
      {open ? <div className="mt-1.5 text-[12.5px] text-white/65 leading-[1.9]">{children}</div> : null}
    </div>
  );
}
Fold.propTypes = { label: PropTypes.string.isRequired, aside: PropTypes.node, children: PropTypes.node };

export default function PriceReceiptCard({ trip }) {
  const main = trip?.[0] || null;
  const bookingId = main?.id || null;
  const bookedAt = main?.created_at || main?.createdAt || null;
  const [state, setState] = useState({ status: 'loading', data: null });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!bookingId) return undefined;
    let alive = true;
    const abort = new AbortController();
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // Closed while the session was read: no request (the trip modal's
        // unmount-safety test holds every child to this).
        if (!alive) return;
        const res = await fetch(`/api/admin/bookings/${encodeURIComponent(bookingId)}/price-receipt`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
          signal: abort.signal,
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
        if (alive) setState({ status: 'ready', data: json });
      } catch {
        if (alive) setState({ status: 'error', data: null });
      }
    })();
    return () => {
      alive = false;
      abort.abort();
    };
  }, [bookingId]);

  if (!bookingId) return null;
  const shell = 'rounded-2xl border px-4 py-3';
  if (state.status === 'loading') {
    return (
      <div className={`${shell} border-white/10 bg-black/20 flex items-center gap-2.5 text-white/55 text-[13px] font-normal`}>
        <Receipt className="w-4 h-4" /> Price receipt <Loader2 className="w-3.5 h-3.5 animate-spin ml-1" />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className={`${shell} border-white/10 bg-black/20 flex items-center gap-2.5 text-white/55 text-[13px] font-normal`}>
        <Receipt className="w-4 h-4" /> Price receipt <span className="text-rose-300/80">Couldn&apos;t load it. Reopen the trip to try again.</span>
      </div>
    );
  }

  const { receipt, chain = [], fare_options: fareOptions = [], before_booking: beforeBooking = [], travelers } = state.data || {};
  if (!receipt) {
    const predates = bookedAt && Date.parse(bookedAt) < Date.parse(RECEIPTS_STARTED_AT);
    return (
      <div className={`${shell} border-white/10 bg-black/20 flex items-center gap-2.5 text-white/55 text-[13px] font-normal`}>
        <Receipt className="w-4 h-4" />
        {predates
          ? 'No price receipt. Booked before receipts started on Oct 28, 2026.'
          : 'No price receipt was saved for this trip.'}
      </div>
    );
  }

  const line = travelerLine(travelers);
  const warn = line.tone === 'warn';
  const search = chain.find((s) => s.kind === 'search') || null;
  const flights = Array.isArray(receipt.flights) ? receipt.flights : [];
  const legs = flights.map((slice) => (Array.isArray(slice) ? slice : [])).filter((s) => s.length);
  const first = legs[0]?.[0];
  const lastLeg = legs[0]?.[legs[0].length - 1];
  const returnFirst = legs[1]?.[0];
  const route = search
    ? `${search.origin} → ${search.destination}`
    : first ? `${first.from} → ${lastLeg?.to || ''}` : '';
  const dates = search
    ? [shortDay(search.departure_date), search.return_date ? shortDay(search.return_date) : null].filter(Boolean).join(' – ')
    : [shortDay(first?.departs), returnFirst ? shortDay(returnFirst.departs) : null].filter(Boolean).join(' – ');
  const searchedWho = search ? travelerSummary(search.travelers) : searchedSummary(receipt.searched_travelers);
  const quote = receipt.server_quote || null;
  const badge = quote?.outcome ? QUOTE_BADGE[quote.outcome] : null;
  const fareRows = fareOptions.flatMap((row) => (Array.isArray(row.results) ? row.results : []));
  const people = Array.isArray(receipt.travelers) ? receipt.travelers : [];

  return (
    <div className={`rounded-2xl border ${warn ? 'border-amber-400/30 bg-amber-500/[0.08]' : 'border-white/10 bg-black/20'}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
      >
        <span className="flex items-start sm:items-center gap-2.5 min-w-0">
          {warn ? <AlertTriangle className="w-4 h-4 mt-0.5 sm:mt-0 text-amber-300 shrink-0" /> : <Receipt className="w-4 h-4 mt-0.5 sm:mt-0 text-white/55 shrink-0" />}
          {/* On a phone the status goes under the title, whole: the warning is
              the line that matters, so it is never cut short. */}
          <span className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2.5">
            <span className="text-white text-[13.5px] font-normal whitespace-nowrap">Price receipt</span>
            <span className={`text-[12px] font-normal ${warn ? 'text-amber-200' : line.tone === 'ok' ? 'text-emerald-300' : 'text-white/45'}`}>
              {line.tone === 'ok' ? <Check className="inline w-3 h-3 -mt-0.5 mr-1" /> : null}
              {line.text}
            </span>
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-white/50 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="px-4 pb-4 space-y-3.5">
          <div className="flex items-center gap-1.5 text-white/45 text-[11.5px] font-normal">
            <Lock className="w-3 h-3" /> Saved {when(receipt.booking_created_at || receipt.created_at)} · can&apos;t be edited
          </div>

          {warn ? (
            <div role="alert" className="rounded-xl border border-amber-400/30 bg-amber-500/[0.08] px-3.5 py-2.5">
              <div className="text-amber-200 text-[13.5px] font-medium">{line.text}</div>
              {SOURCE_LABEL[travelers?.source] ? (
                <div className="text-amber-200/75 text-[12px] font-normal mt-0.5">Priced count from {SOURCE_LABEL[travelers.source]}</div>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Box label="Searched">
              <div className="text-white text-[14px] font-normal">{[route, dates].filter(Boolean).join(' · ') || '—'}</div>
              <div className="text-white/60 text-[12.5px] font-normal mt-0.5">
                {[cap(search?.cabin || receipt.searched?.cabin_class), searchedWho, search ? timeOnly(search.created_at) : null].filter(Boolean).join(' · ') || 'No search on record'}
              </div>
            </Box>
            <Box label="Fare booked">
              <div className="text-white text-[14px] font-normal">{legs.map((leg) => leg.map((s) => s.flight).join(' + ')).join(' · ') || '—'}</div>
              <div className="text-white/60 text-[12.5px] font-normal mt-0.5">
                {[receipt.fare_brand || receipt.cabin_class, receipt.seller ? `sold by ${receipt.seller}` : null].filter(Boolean).join(' · ')}
              </div>
            </Box>
          </div>

          <div>
            <div className="text-white/50 text-[11.5px] font-normal mb-1.5">Prices</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Price label="Listed in search" cents={receipt.listed_fare_cents} />
              <Price label="Airfare charged" cents={receipt.fare_cents} />
              <Price label="Service fee" cents={receipt.service_fee_cents} />
              <Price label="Hold fee" cents={receipt.hold_fee_cents} />
              <Price label="Trip total" cents={receipt.trip_total_cents} strong />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] pt-2.5">
            <div>
              <div className="text-white/50 text-[11.5px] font-normal">Server price check at checkout</div>
              <div className="text-white text-[13.5px] font-normal mt-0.5">
                {!quote
                  ? 'No check on record for this trip'
                  : quote.status === 'ok'
                    ? [`${money(quote.quoted_fare_cents)} for ${plural(Number(quote.travelers?.priced) || 0, 'traveler', 'travelers')}`, quote.fare_brand].filter(Boolean).join(' · ')
                    : `Couldn't price it (${String(quote.reason || quote.status).replace(/_/g, ' ')})`}
              </div>
            </div>
            {badge ? <span className={`rounded-full px-2.5 py-0.5 text-[11.5px] font-normal shrink-0 ${badge.cls}`}>{badge.label}</span> : null}
          </div>

          <div className="border-t border-white/[0.06] pt-2.5">
            <div className="text-white/50 text-[11.5px] font-normal mb-1.5">
              Travelers on the booking ({people.length || travelers?.on_booking || 0}) · age at departure
            </div>
            {people.length ? (
              <div className="flex flex-wrap gap-1.5">
                {people.map((p, i) => (
                  <span key={`${p.given_name}-${i}`} className="rounded-full bg-white/[0.05] px-2.5 py-0.5 text-white text-[12.5px] font-normal">
                    {[p.given_name, p.family_name].filter(Boolean).join(' ')}{Number.isFinite(p.age_at_departure) ? ` · ${p.age_at_departure}` : ''}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-white/45 text-[12.5px] font-normal">Not recorded</div>
            )}
          </div>

          <div className="border-t border-white/[0.06]">
            <Fold label={`Searches before booking (${beforeBooking.length})`} aside="Last 2 hours">
              {beforeBooking.length ? beforeBooking.map((s) => (
                <div key={s.id} className="flex justify-between gap-3">
                  <span>{[timeOnly(s.created_at), s.kind === 'search' ? `${s.origin} → ${s.destination}` : s.kind === 'return' ? 'Return flights' : 'Fares', travelerSummary(s.travelers)].filter(Boolean).join(' · ')}</span>
                  <span className="shrink-0">{Number.isFinite(s.lowest_price_cents) ? `from ${wholeMoney(s.lowest_price_cents)}` : ''}</span>
                </div>
              )) : <div className="text-white/45">No searches on record</div>}
            </Fold>
            <Fold label={`Fares shown for this flight (${fareRows.length})`}>
              {fareRows.length ? fareRows.map((f, i) => {
                const picked = f.fare && receipt.fare_brand && f.fare === receipt.fare_brand && (!receipt.seller || f.seller === receipt.seller);
                return (
                  <div key={`${f.fare}-${f.seller}-${i}`} className={`flex justify-between gap-3 ${picked ? 'text-white' : ''}`}>
                    <span>{[f.fare || 'Fare', f.seller].filter(Boolean).join(' · ')}{picked ? ' (picked)' : ''}{f.separate_tickets ? ' · separate tickets' : ''}</span>
                    <span className="shrink-0">{wholeMoney(f.price_cents)}</span>
                  </div>
                );
              }) : <div className="text-white/45">No fares on record</div>}
            </Fold>
          </div>
        </div>
      ) : null}
    </div>
  );
}

PriceReceiptCard.propTypes = {
  trip: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    created_at: PropTypes.string,
    createdAt: PropTypes.string,
  })),
};
